import type { InputMaybe, Segment_Bool_Exp } from '@openx/types/gqlTypes.ts';
import type { Provider } from '@openx/types/provider.ts';
import type { ExportTypeValue } from '../../../../constants.ts';
import { US_REGION_CODE } from '../../../../constants.ts';
import type { SegmentFiltersQueryParams } from '../../components/drawer/components/SegmentsList/types.ts';
import { type DataSourceOptions, dataSourceOptions } from '../../components/drawer/components/types.ts';
import { buildDirectProviderFilters } from './buildDirectProviderFilters.ts';
import { getAlwaysAppliedFilters } from './defaultFilters.ts';
import {
  buildCategoryFilters,
  buildDataTypeFilters,
  buildPartnerProviderFilters,
  buildPhraseFilter,
  buildRangeFilters,
  buildShowMySegmentsFilters,
  buildShowOXSProprietarySegmentsFilters,
  buildThirdPartyDataSegmentsFilters,
} from './filterGroups.ts';
import { clean, type WhereCondition } from './filterHelpers.ts';
import { buildWithoutProviderIdStrategy, buildWithProviderIdStrategy } from './filterStrategies.ts';

export type BuildSegmentFiltersParams = {
  filters: SegmentFiltersQueryParams;
  accountId: string;
  providerId?: string;
  linkedProviders: Provider[];
  isOaMatchType: boolean;
  marketContext?: {
    regionCode: string | null;
    countries: string[];
  };
  exportType?: ExportTypeValue;
  dataSourceFilters: DataSourceOptions;
};

const buildUsMarketPrefilter = (): WhereCondition => {
  const regionMatch = {
    regions: {
      region_code: {
        _eq: US_REGION_CODE,
      },
    },
  };

  const countryRegionMatch = {
    countries: {
      country: {
        region_code: {
          _eq: US_REGION_CODE,
        },
      },
    },
  };

  return {
    _or: [
      regionMatch,
      countryRegionMatch,
      {
        _and: [
          {
            _not: {
              countries: {},
            },
          },
          {
            _not: {
              regions: {},
            },
          },
        ],
      },
    ],
  };
};

const buildMarketPrefilter = (marketContext?: BuildSegmentFiltersParams['marketContext']): WhereCondition => {
  if (!marketContext) {
    return buildUsMarketPrefilter();
  }

  const selectedCountries = marketContext.countries.filter(Boolean);

  if (selectedCountries.length > 0) {
    return {
      _or: [
        {
          countries: {
            country_code: {
              _in: selectedCountries,
            },
          },
        },
        {
          _and: [
            {
              regions: {
                region_code: {
                  _eq: marketContext.regionCode,
                },
              },
            },
            {
              _not: {
                countries: {},
              },
            },
          ],
        },
      ],
    };
  }

  if (!marketContext.regionCode) {
    return undefined;
  }

  const regionMatch = {
    regions: {
      region_code: {
        _eq: marketContext.regionCode,
      },
    },
  };

  const countryRegionMatch = {
    countries: {
      country: {
        region_code: {
          _eq: marketContext.regionCode,
        },
      },
    },
  };

  // US market includes US-mapped segments OR legacy segments with empty region/country arrays.
  if (marketContext.regionCode === US_REGION_CODE) {
    return buildUsMarketPrefilter();
  }

  return {
    _or: [regionMatch, countryRegionMatch],
  };
};

export type BuildSegmentVisibilityWhereParams = {
  accountId: string;
  providerId?: string;
  isOaMatchType: boolean;
  exportType?: ExportTypeValue | null;
};

export const buildSegmentVisibilityWhere = ({
  accountId,
  providerId,
  isOaMatchType,
  exportType,
}: BuildSegmentVisibilityWhereParams): InputMaybe<Segment_Bool_Exp> => {
  const alwaysFilters = getAlwaysAppliedFilters(exportType);
  const directProviderFilters = buildDirectProviderFilters(accountId, providerId);

  let whereClause: WhereCondition;

  if (!isOaMatchType && providerId && directProviderFilters) {
    whereClause = buildWithProviderIdStrategy({
      alwaysFilters,
      directProviderFilters,
      dataTypeFilters: [],
      filterGroupClauses: [],
      accountId,
      marketPrefilter: undefined,
    });
  } else {
    whereClause = buildWithoutProviderIdStrategy({
      alwaysFilters,
      accountId,
      providerId,
      dataTypeFilters: [],
      filterGroupClauses: [],
      isOaMatchType,
      marketPrefilter: undefined,
    });
  }

  return clean(whereClause) as InputMaybe<Segment_Bool_Exp>;
};

export const buildSegmentFilters = ({
  filters,
  accountId,
  providerId,
  linkedProviders,
  isOaMatchType,
  marketContext,
  exportType,
  dataSourceFilters,
}: BuildSegmentFiltersParams) => {
  const alwaysFilters = getAlwaysAppliedFilters(exportType);
  const marketPrefilter = buildMarketPrefilter(marketContext);

  const filterGroupClauses: WhereCondition[] = [
    buildPhraseFilter(filters),
    ...buildRangeFilters(filters),
    ...buildCategoryFilters(filters),
    ...buildPartnerProviderFilters(filters),
    ...(dataSourceFilters === dataSourceOptions.MyData ? buildShowMySegmentsFilters(accountId, linkedProviders) : []),
    ...(dataSourceFilters === dataSourceOptions.OpenXSelectData ? buildShowOXSProprietarySegmentsFilters() : []),
    ...(dataSourceFilters === dataSourceOptions.oxs3pData ? buildThirdPartyDataSegmentsFilters(accountId) : []),
  ].filter(Boolean);

  // Get data type filters
  const dataTypeFilters: WhereCondition[] = buildDataTypeFilters(filters, accountId, isOaMatchType);

  // Build where clause based on providerId
  let whereClause: WhereCondition;
  const directProviderFilters = buildDirectProviderFilters(accountId, providerId);

  if (!isOaMatchType && providerId && directProviderFilters) {
    whereClause = buildWithProviderIdStrategy({
      alwaysFilters,
      directProviderFilters,
      dataTypeFilters,
      filterGroupClauses,
      accountId,
      marketPrefilter,
    });
  } else {
    whereClause = buildWithoutProviderIdStrategy({
      alwaysFilters,
      accountId,
      providerId,
      dataTypeFilters,
      filterGroupClauses,
      isOaMatchType,
      marketPrefilter,
    });
  }

  // Cast the where clauses to ensure they're compatible with GraphQL input types
  const cleanedWhereClause = clean(whereClause) as InputMaybe<Segment_Bool_Exp>;

  return {
    where: cleanedWhereClause,
    whereAggregate: cleanedWhereClause,
    order_by: { name: 'asc' },
    limit: filters.limit,
    offset: filters.offset,
  };
};
